import React, { forwardRef, useRef } from "react";
import type { ShowcaseContent } from "../../types.ts";
import { Helix } from "../Helix.tsx";
import { StatusPill } from "../common.tsx";
import { IconArrowRight, IconChevDown, IconPlay } from "../icons.tsx";
import { useParallax } from "../../hooks/useParallax.ts";
import { platformShortLabel, splitTagline } from "../../utils.tsx";

interface HeroSlideProps {
  content: ShowcaseContent;
  showHelix: boolean;
  onJumpTo: (title: string) => void;
}

function HeroTitle({ tagline }: { tagline: string }) {
  const split = splitTagline(tagline);
  return (
    <h1 className="dz-hero-h1">
      {split ? split.head + " " : tagline}
      {split?.tail && <em>{split.tail}</em>}
    </h1>
  );
}

export const HeroSlide = forwardRef<HTMLElement, HeroSlideProps>(function HeroSlide(
  { content, showHelix, onJumpTo },
  ref
) {
  const orbRef = useRef<HTMLDivElement | null>(null);
  const sectionRef = useRef<HTMLElement | null>(null);

  useParallax(sectionRef, orbRef);

  const platformShort = platformShortLabel(content.app.platformRole);
  const status = content.app.status ?? "mvp";
  const appImage = content.app.appImage;

  return (
    <section
      ref={(node) => {
        sectionRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLElement | null>).current = node;
      }}
      className="dz-slide dz-slide--hero"
    >
      <div className="dz-hero-glow" aria-hidden="true" />

      <div className="dz-container dz-hero-content">

        {/* ── Row 1: text left | DNA right ── */}
        <div className="dz-hero-top-row">

          {/* Left: text */}
          <div className="dz-hero-text">
            <div className="dz-hero-meta">
              <span className="dz-mod-badge">
                {content.app.name}
              </span>
              <StatusPill status={status} />
              <span className="dz-hero-platform">{platformShort}</span>
            </div>
            <HeroTitle tagline={content.app.tagline} />
            <p className="dz-hero-lede">{content.app.oneLiner}</p>
            <div className="dz-hero-actions">
              <button type="button" className="dz-hero-cta" onClick={() => onJumpTo("Approach")}>
                <span className="dz-hero-cta__spark"><IconPlay fill="white" /></span>
                <span>See how it works</span>
                <IconArrowRight className="dz-hero-cta__arr" />
              </button>
              <button type="button" className="dz-hero-link" onClick={() => onJumpTo("Full Surface")}>
                View capabilities
                <IconChevDown className="dz-hero-link__arr" />
              </button>
            </div>
          </div>

          {/* Right: single DNA orb */}
          <div className="dz-hero-dna" aria-hidden="true" ref={orbRef}>
            <div className="dz-dna-orb">
              <div className="dz-orb-ring dz-orb-ring--outer" />
              <div className="dz-orb-ring dz-orb-ring--mid" />
              <div className="dz-orb-ring dz-orb-ring--inner" />
              <svg className="dz-orb-lines" viewBox="0 0 500 500" preserveAspectRatio="xMidYMid meet">
                <line x1="250" y1="250" x2="450" y2="60"  stroke="url(#dz-strandL)" strokeWidth="1.5" opacity="0.55" />
                <line x1="250" y1="250" x2="470" y2="380" stroke="url(#dz-strandR)" strokeWidth="1.5" opacity="0.55" />
                <line x1="250" y1="250" x2="40"  y2="450" stroke="url(#dz-strandL)" strokeWidth="1.5" opacity="0.55" />
                <line x1="250" y1="250" x2="20"  y2="120" stroke="url(#dz-strandR)" strokeWidth="1.5" opacity="0.55" />
              </svg>
              {showHelix && <div className="dz-orb-center"><Helix /></div>}
              <div className="dz-orb-particles">
                <span className="dz-orb-dot" style={{ top: "18%", left: "22%",  animationDelay: "-0.3s" }} />
                <span className="dz-orb-dot" style={{ top: "35%", right: "16%", animationDelay: "-1.6s" }} />
                <span className="dz-orb-dot" style={{ top: "55%", left: "14%",  animationDelay: "-2.8s" }} />
                <span className="dz-orb-dot" style={{ bottom: "18%", right: "24%", animationDelay: "-4.1s" }} />
                <span className="dz-orb-dot" style={{ top: "48%", right: "8%",  animationDelay: "-1.0s" }} />
              </div>
            </div>
          </div>

        </div>

        {/* ── Row 2: full-width screenshot ── */}
        {appImage && (
          <div className="dz-hero-screenshot-wrap">
            <img
              src={appImage}
              alt={`${content.app.name} screenshot`}
              className="dz-hero-screenshot"
              draggable={false}
            />
          </div>
        )}

      </div>
    </section>
  );
});

